import { FC, FormEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box, Typography, Modal, Paper } from '@mui/material';

import { COMMITMENT_TYPE_OPTIONS, COMPENSATION_TYPE_OPTIONS } from '@libs/types';
import { Job } from '@libs/models';
import { JobUpdateDto } from '@libs/dto';
import {
  TextInput,
  Select,
  RooftopSelect,
  CheckBoxInput,
  FreeSoloInput,
  RichTextEditor,
  CheckboxListInput,
  JobPreview,
} from '@carcareers/components';
import { useForm } from '@carcareers/contexts';
import { useSnackbar } from '@carcareers/contexts';
import { JOB_POSITIONS, BENEFITS } from '@carcareers/utils';
import { useJob } from '@carcareers/hooks';
interface JobUpdateFormProps {
  onSuccess?: () => void;
  usePostData?: boolean;
}

export const JobUpdateForm: FC<JobUpdateFormProps> = ({ onSuccess, usePostData = true }) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const { data, setData, setErrors } = useForm<JobUpdateDto>();
  const { updateJob } = useJob();
  const [preview, setPreview] = useState(false);
  const [rooftopDefaultValue, setRooftopDefaultValue] = useState<{ label: string; value: number | any } | undefined>();

  useEffect(() => {
    if (data?.rooftop && !rooftopDefaultValue) {
      // Only set the default value once initially
      if (typeof data.rooftop === 'object' && data.rooftop !== null) {
        setRooftopDefaultValue({
          label: `${data.rooftop.id} - ${data.rooftop.name || 'Unknown Rooftop'}`,
          value: data.rooftop,
        });
      } else if (typeof data.rooftop === 'number') {
        setRooftopDefaultValue({
          label: 'Loading...',
          value: data.rooftop,
        });
      }
    }
  }, [data?.rooftop, rooftopDefaultValue]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const transformedData = {
      ...data,
      posts: usePostData ? data.posts : undefined,
      compensationMin: Number(data.compensationMin),
      compensationMax: Number(data.compensationMax),
    };

    if (transformedData.compensationMin > transformedData.compensationMax) {
      setErrors([
        {
          property: 'compensationMin',
          errors: ['Minimum compensation must be less than or equal to maximum compensation'],
        },
        {
          property: 'compensationMax',
          errors: ['Maximum compensation must be greater than or equal to minimum compensation'],
        },
      ]);
      toggleSnackbar('error', 'Invalid compensation values');
      return;
    }

    const response = await updateJob(transformedData, { onSuccess });

    if (response?.data) {
      setErrors([]);
    } else if (response?.errors) {
      setErrors(response.errors);
    }
  };

  return (
    <>
      <TextInput required label="Title" name="title" sx={{ borderRadius: 2 }} />
      <RooftopSelect name="rooftop" defaultValue={rooftopDefaultValue} sx={{ borderRadius: 2 }} />
      <Typography variant="h5">Compensation</Typography>
      <Select
        label="Commitment Type"
        name="commitmentType"
        options={COMMITMENT_TYPE_OPTIONS}
        sx={{ borderRadius: 2 }}
      />
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextInput fullWidth label="($) Min" name="compensationMin" type="number" sx={{ borderRadius: 2 }} />
        <TextInput fullWidth label="($) Max" name="compensationMax" type="number" sx={{ borderRadius: 2 }} />
        <Select
          fullWidth
          label="Per"
          name="compensationType"
          options={COMPENSATION_TYPE_OPTIONS}
          sx={{ borderRadius: 2 }}
        />
      </Box>
      <CheckBoxInput label="Commission" name="hasCommission" />
      <Typography variant="h5">Keywords</Typography>
      <FreeSoloInput
        multiple
        disableCloseOnSelect
        label="Keywords"
        name="keywords"
        suggestions={JOB_POSITIONS}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.position)}
        getOptionValue={(option) => (typeof option === 'string' ? option : option.position)}
        groupBy={(option) => (typeof option === 'string' ? 'Positions' : option.department)}
        value={data?.keywords}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: 2,
            gap: 1,
          },
        }}
      />
      <Typography variant="h5">Benefits</Typography>
      <CheckboxListInput
        name="benefits"
        variant="chip"
        options={BENEFITS.map((benefit) => ({ label: benefit.name, value: benefit.value }))}
      />
      <Typography variant="h5">Description</Typography>
      <RichTextEditor data={data?.description} onUpdate={(value) => setData('description', value)} />
      <Modal open={preview} onClose={() => setPreview(false)}>
        <Paper
          sx={{
            overflowY: 'scroll',
            position: 'absolute',
            top: 100,
            left: 100,
            bottom: 100,
            right: 100,
            p: { xs: 2, sm: 2, md: 4 },
          }}
        >
          <JobPreview data={data as Job} />
        </Paper>
      </Modal>
      <Box
        sx={(theme) => ({
          display: 'flex',
          gap: theme.spacing(2),
          justifyContent: 'flex-end',
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
          [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
        })}
      >
        <Button size="large" variant="outlined" color="secondary" onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={() => setPreview(true)}>
          Preview
        </Button>
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Update Job
        </Button>
      </Box>
    </>
  );
};

export default JobUpdateForm;
